/**
 * Arrival-touch capture — parse the landing URL's query string +
 * referrer into the bounded, scrubbed shape the server accepts
 * (`context.utm` / `context.params` / `context.referrer` on /decide and
 * /chat). How a visitor got here is intent: `utm_term` is the literal
 * phrase they typed into a search engine; a bare `gclid` marks a paid
 * Google click even when no utm_* rode along (Ads auto-tagging);
 * site-native params (`plan=enterprise`, `topic=fedramp`) encode what
 * they came to look at. The server renders these legibly and lets the
 * LLM infer — no mapping tables.
 *
 * SCRUB CONTRACT (privacy boundary — this module is the only gate
 * between window.location.search and our DB/LLM):
 *   - our own sp_* params never leave the page
 *   - credential-shaped keys (token / auth / code / sig / key / …) dropped
 *   - any value containing `@` dropped (ESP links carry ?email=…)
 *   - long opaque values dropped UNLESS the key is a known click ID
 *   - everything capped: keys 64 chars, values 200, ≤10 utm + ≤12 params
 */

/** Click-ID keys — kept verbatim despite being token-shaped: their
 *  PRESENCE decodes the paid channel, and the raw ID enables offline-
 *  conversion upload once an Ads connector exists. */
const CLICK_ID_KEYS = new Set([
  'gclid', // Google Ads
  'gbraid', // Google Ads (iOS, app-to-web)
  'wbraid', // Google Ads (iOS, web-to-app)
  'fbclid', // Meta
  'msclkid', // Microsoft Ads
  'li_fat_id', // LinkedIn
  'ttclid', // TikTok
  'twclid', // X
]);

/** Key fragments that mark a credential/secret-bearing param. Checked
 *  as substrings of the lowercased key. */
const DENY_KEY_FRAGMENTS = [
  'token',
  'auth',
  'secret',
  'passw',
  'apikey',
  'api_key',
  'session',
  'signature',
  'email',
  'jwt',
  'nonce',
];
// `code` exact (OAuth) — as a fragment it would eat promo/discount codes,
// which are legit intent. `key` exact — as a fragment it would eat `keyword`.
const DENY_EXACT_KEYS = new Set(['sig', 'key', 'code', 'state', 'hash', 'id_token']);

const MAX_UTM_KEYS = 10;
const MAX_PARAM_KEYS = 12;
const MAX_KEY_LEN = 64;
const MAX_VALUE_LEN = 200;
/** Non-click-id values longer than this that look like one opaque blob
 *  (no spaces) are dropped — subscriber hashes, encoded payloads. */
const MAX_OPAQUE_LEN = 64;

export interface PageTouch {
  /** utm_* params, `utm_` prefix stripped: source/medium/campaign/term/content/… */
  utm?: Record<string, string>;
  /** Scrubbed non-utm landing params, click IDs included. */
  params?: Record<string, string>;
  referrer?: string;
}

function deniedKey(key: string): boolean {
  if (DENY_EXACT_KEYS.has(key)) return true;
  return DENY_KEY_FRAGMENTS.some((frag) => key.includes(frag));
}

/**
 * Pure core — takes the raw query string + referrer so tests don't need
 * a DOM. `readPageContext()` in index.tsx binds it to window.location.
 */
export function parsePageTouch(search: string, referrer: string): PageTouch {
  const out: PageTouch = {};
  try {
    const utm: Record<string, string> = {};
    const params: Record<string, string> = {};
    let utmCount = 0;
    let paramCount = 0;
    for (const [rawKey, rawValue] of new URLSearchParams(search)) {
      const key = rawKey.toLowerCase().slice(0, MAX_KEY_LEN);
      const value = rawValue.trim().slice(0, MAX_VALUE_LEN);
      if (!key || !value) continue;
      if (key.startsWith('sp_')) continue; // our own plumbing
      if (key.startsWith('utm_')) {
        const name = key.slice(4);
        if (name && utmCount < MAX_UTM_KEYS && !(name in utm)) {
          utm[name] = value;
          utmCount++;
        }
        continue;
      }
      if (paramCount >= MAX_PARAM_KEYS || key in params) continue;
      if (CLICK_ID_KEYS.has(key)) {
        params[key] = value;
        paramCount++;
        continue;
      }
      if (deniedKey(key)) continue;
      if (value.includes('@')) continue; // email-bearing (ESP links)
      if (value.length > MAX_OPAQUE_LEN && !value.includes(' ')) continue;
      params[key] = value;
      paramCount++;
    }
    if (utmCount > 0) out.utm = utm;
    if (paramCount > 0) out.params = params;
    if (referrer) out.referrer = referrer.slice(0, 2000);
  } catch {
    // Never break the visitor flow on tracking.
  }
  return out;
}
